// Space Grotesk v5
// 1 Style: normal
// 3 Subsets: latin, latin - ext, vietnamese
// 5 Weights: 300, 400, 500, 600, 700
// https://fontsource.org/#space-grotesk
import "@fontsource/space-grotesk/400.css" // Weight normal
import "@fontsource/space-grotesk/500.css" // Weight medium
import "@fontsource/space-grotesk/600.css" // Weight semibold
import "@fontsource/space-grotesk/700.css" // Weight bold

// Space Mono
// v6
// 2 Styles: italic, normal
// 3 Subsets: latin, latin - ext, vietnamese
// 2 Weights: 400, 700
// https://fontsource.org/#space-mono
import "@fontsource/space-mono/400.css" // Weight normal
import "@fontsource/space-mono/700.css" // Weight bold

// Fraunces
// 2 Styles: italic, normal
// 2 Weights: 100, 200, 300, 400, 500, 600, 700, 800, 900
// https://fontsource.org/fonts/fraunces
import "@fontsource/fraunces/300.css" // Weight light
import "@fontsource/fraunces/400.css" // Weight normal

import Alpine from 'alpinejs'
import 'lazysizes'
import 'focus-visible'


import {initScrollSpy} from './scrollSpy'
import {initMobileScrollSpy} from './mobileScrollSpy'
import {
  testimonialSlider,
  pageSlider,
  programSlider,
  filterSlider,
  cohortTextSlider,
} from "./sliders";
import {columns} from "./columns";
import {salScrollAnimate} from "./sal";
import {getDeviceType} from "./getDeviceType";
import {getCookie} from "./getCookie";
import {setCookie} from "./setCookie";
import {
  gtmChatEvent,
  gtmFormsEvent,
  gtmPushVisibleFields,
  gtmFormFieldFocusListener,
  gtmPushInPageNavigationOnClick,
  gtmPushProgramOnClick,
  gtmPushFormEventOnView,
  gtmPushFormEventsOnClick,
  gtmPushFormEventsOnFocusout,
  gtmSaveFieldValuesForSubmit,
  gtmPushFormEventsOnSubmit,
  gtmAttachClassesToDomElements,
  gtmDetectWistiaIframeAndAttachFiringEvents,
  gtmDetectYoutubeIframeAndAttachFiringEvents,
  gtmPushEventsOnPageLoad,
  gtmPushEventsOnElementClick,
  gtmPushDigitalExperience,
} from "./gtmUtilities";
import {setPrefillFormData, initializeDataPrefill, formsApi, processOtpUrlParam} from "./formPrefill"
import {
  chooseSelectOption,
  chooseRadioOption,
  getConditionalRedirectUrl,
  getFinalRedirectOverwrite,
  handleImmediateRedirectBasedOnFormValues,
  lockSubmit,
  unlockSubmit,
  addPartnerParameterToUrlParams,
  addQueryStringToUrl,
  getInputValue,
  getDefaultFormParams,
  filterStepsInFormNavigation,
  parseStepsHidingConfig,
  checkIfUserIsEligible
} from "./forms";
import {cohortSelectorSupport} from "./cohortSelectorSupport";
import {accMarketSelectorSupport} from "./accMarketSelectorSupport";
import {
  initPhoneInputs,
  loadLibPhone,
  checkEmailInputValidation,
  checkPhoneInputValidation
} from "./formInputs";
import {getAssessmentLink, getAssessmentParamsFromEmail} from "./assessments";
import {formFieldsStorage} from "./formFieldsStorage";
import {updateCohortStatusAfterDeadline} from "./cohortCards";

//-------------------------------------------------------------
// attach fullstackacademy object to the window object
//-------------------------------------------------------------
window.dataLayer = window.dataLayer || [];

window.fullstackacademy = window.fullstackacademy || {};

Object.assign(window.fullstackacademy, {
  initScrollSpy,
  initMobileScrollSpy,
  testimonialSlider,
  pageSlider,
  programSlider,
  cohortTextSlider,
  filterSlider,
  columns,
  salScrollAnimate,
  getDeviceType,
  getCookie,
  gtmFormsEvent,
  gtmPushVisibleFields,
  gtmFormFieldFocusListener,
  gtmPushInPageNavigationOnClick,
  gtmPushProgramOnClick,
  gtmPushFormEventOnView,
  gtmPushFormEventsOnClick,
  gtmPushFormEventsOnFocusout,
  gtmSaveFieldValuesForSubmit,
  gtmPushFormEventsOnSubmit,
  gtmAttachClassesToDomElements,
  gtmDetectWistiaIframeAndAttachFiringEvents,
  gtmDetectYoutubeIframeAndAttachFiringEvents,
  gtmPushEventsOnPageLoad,
  setCookie,
  getAssessmentLink,
  formFieldsStorage,
  updateCohortStatusAfterDeadline,
  gtmPushEventsOnElementClick,
  gtmPushDigitalExperience,
  lockSubmit,
  unlockSubmit
})

/**
 * Hubspot events, most chat.
 * docs: https://legacydocs.hubspot.com/docs/methods/conversations_api/hubspot-conversations-javascript-api
 */
function onConversationsAPIReady() {

  window.HubSpotConversations.on('conversationStarted', payload => {
    gtmChatEvent(
      "Conversation Started",
      payload.conversationId);
  });

  window.HubSpotConversations.on('conversationClosed', payload => {
    gtmChatEvent(
      "Conversation Closed",
      payload.conversationId,
    );
  });

  window.HubSpotConversations.on('unreadConversationCountChanged', payload => {
    gtmChatEvent(
      "Unread Conversations",
      payload.unreadCount
    );
  });
}

// json object support in localStorage
Storage.prototype.setObject = function (key, value) {
  this.setItem(key, JSON.stringify(value));
}

Storage.prototype.getObject = function (key) {
  const value = this.getItem(key);
  if (value && value !== 'undefined') {
    return JSON.parse(value);
  }
  return null
}

if (window.HubSpotConversations) {
  onConversationsAPIReady();
} else {
  window.hsConversationsOnReady = [onConversationsAPIReady];
}

// initialize Alpine
window.Alpine = Alpine
document.addEventListener("DOMContentLoaded", function () {
  Alpine.start()
})

// attach GTM classes to DOM elements
document.addEventListener("DOMContentLoaded", function () {
  document.addEventListener('lazybeforeunveil', (event) => {
    const backgroundImageUrl = event.target.getAttribute('data-bg');
    if (backgroundImageUrl) {
      event.target.style.backgroundImage = 'url(' + backgroundImageUrl + ')';
    }

    if (event.target.dataset.src && event.target.dataset.src.includes('youtube')) {
      event.target.dataset.src = `${event.target.dataset.src}?&enablejsapi=1&origin=${window.location.origin}`
    }
  })
  document.addEventListener('lazyloaded', function (event) {
    if (event.target.dataset.src && event.target.dataset.src.includes('wistia')) {
      window.fullstackacademy.gtmDetectWistiaIframeAndAttachFiringEvents('iframe[data-src*="wistia"]')
    }
    if (event.target.dataset.src && event.target.dataset.src.includes('youtube')) {
      window.fullstackacademy.gtmDetectYoutubeIframeAndAttachFiringEvents('iframe[data-src*="youtube"]')
    }
  })
  window.fullstackacademy.gtmAttachClassesToDomElements()
  window.fullstackacademy.gtmDetectWistiaIframeAndAttachFiringEvents()
  window.fullstackacademy.gtmDetectYoutubeIframeAndAttachFiringEvents()
  window.fullstackacademy.gtmPushEventsOnPageLoad()
  window.fullstackacademy.gtmPushEventsOnElementClick()
})

// get device type and save to cookies
document.addEventListener("DOMContentLoaded", function () {
  const deviceType = window.fullstackacademy.getDeviceType()
  window.fullstackacademy.setCookie('deviceType', deviceType, 180)
})